import { confluenceLocalStorageInstance as localStorage } from '@confluence/storage-manager/entry-points/storageInstance';
import { PERSISTED_KEYS_ON_SERVER } from '@confluence/storage-manager/entry-points/LocalStorageKeys';
import { getLogger } from '@confluence/logger';

const logger = getLogger('SmartCreateFeedbackBanner');

interface LocalStorageItem {
	time?: number;
	count?: number;
}
const MAX_DISMISS_COUNT = 3;
const ONE_DAY_IN_MS = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
const TWO_WEEKS_IN_MS = 14 * ONE_DAY_IN_MS; // 2 weeks in milliseconds
const STORAGE_KEY = PERSISTED_KEYS_ON_SERVER.PERSISTED_SMART_CREATE_FEEDBACK_BANNER_LAST_DISMISSED;
const DISMISSAL_PERIODS = [
	0, // Initial state, no dismissal period
	ONE_DAY_IN_MS, // 1st dismissal: 24 hours
	TWO_WEEKS_IN_MS, // 2nd dismissal: 2 weeks
];

const getLocalStorageItem = (): LocalStorageItem => {
	try {
		const itemString = localStorage.getItem(STORAGE_KEY);
		if (!itemString) {
			return {};
		}
		const item = JSON.parse(itemString);
		if (
			typeof item === 'object' &&
			item !== null &&
			!Array.isArray(item) &&
			(typeof item.time === 'number' || item.time === undefined) &&
			(typeof item.count === 'number' || item.count === undefined)
		) {
			return item;
		}
	} catch (error) {
		logger.error`Error parsing local storage key(${STORAGE_KEY}): ${error}`;
	}
	localStorage.setItem(STORAGE_KEY, JSON.stringify({}));
	return {};
};

const updateLocalStorageItem = (item: LocalStorageItem) => {
	try {
		localStorage.setItem(STORAGE_KEY, JSON.stringify(item));
	} catch (error) {
		logger.error`Error setting local storage key(${STORAGE_KEY}): ${error}`;
	}
};

export const isBannerDismissed = (): boolean => {
	const { time = 0, count = 0 } = getLocalStorageItem();
	const currentTime = Date.now();
	if (count >= MAX_DISMISS_COUNT) {
		return true; // Permanently dismissed
	}
	const requiredDuration = DISMISSAL_PERIODS[count] ?? 0;
	return currentTime - time < requiredDuration;
};

const updateBannerDismissal = (count: number) => {
	const item = getLocalStorageItem();
	const currentTime = Date.now();
	item.time = currentTime;
	item.count = count;
	updateLocalStorageItem(item);
};

export const dismissBanner = () => {
	const item = getLocalStorageItem();
	const newCount = (item.count ?? 0) + 1;
	if (newCount >= MAX_DISMISS_COUNT) {
		permanentlyDismissBanner();
	} else {
		updateBannerDismissal(newCount);
	}
};

export const permanentlyDismissBanner = () => {
	updateBannerDismissal(MAX_DISMISS_COUNT);
};
